/**
 * App-scoped global store (ADR-0043 — the `$global` namespace).
 *
 * Page variables (`$page`) are scoped to ONE page and die on navigation. The
 * store is the Appsmith `appsmith.store` equivalent: a key/value bag that
 * **survives navigation** within an app (it lives at module scope, OUTSIDE the
 * per-page React tree), so a wizard / cart / cross-page selection persists.
 * Keyed by app slug so two apps never share state.
 *
 * Bindings read it as `{{ $global.<key> }}`; actions write it via
 * `global_value` / `global_remove` / `global_clear`. The snapshot is a STABLE
 * object reference between writes (replace-on-write), so `useSyncExternalStore`
 * can use it directly without an infinite render loop.
 */

const stores: Record<string, Record<string, unknown>> = {};
const EMPTY: Readonly<Record<string, unknown>> = Object.freeze({});
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

/** The current store for an app — a stable ref that only changes on a write. */
export function storeSnapshot(appSlug: string): Record<string, unknown> {
  return stores[appSlug] ?? (EMPTY as Record<string, unknown>);
}

/** Set a key (replace-on-write keeps the snapshot ref stable until now). */
export function storeValue(appSlug: string, key: string, value: unknown): void {
  stores[appSlug] = { ...(stores[appSlug] ?? {}), [key]: value };
  emit();
}

/** Seed the DECLARED `$global` variables (WS-4a, `apps.global_state`) with their
 *  initial values — but ONLY keys not already present, so a value the user wrote
 *  during the session survives navigation / re-mount (idempotent). Batches into
 *  ONE write (single `emit`) and is a no-op when nothing new is added. */
export function seedGlobals(
  appSlug: string,
  entries: { name: string; initial?: unknown }[],
): void {
  const cur = stores[appSlug] ?? {};
  let next: Record<string, unknown> | null = null;
  for (const e of entries) {
    if (!(e.name in cur)) (next ??= { ...cur })[e.name] = e.initial ?? null;
  }
  if (next) {
    stores[appSlug] = next;
    emit();
  }
}

/** Delete a key (no-op + no notify if absent, to avoid spurious re-renders). */
export function removeValue(appSlug: string, key: string): void {
  const cur = stores[appSlug];
  if (!cur || !(key in cur)) return;
  const next = { ...cur };
  delete next[key];
  stores[appSlug] = next;
  emit();
}

/** Clear an app's whole store (no-op + no notify if already empty). */
export function clearStore(appSlug: string): void {
  const cur = stores[appSlug];
  if (!cur || Object.keys(cur).length === 0) return;
  stores[appSlug] = {};
  emit();
}

/** Subscribe to ANY store write; returns an unsubscribe fn. */
export function subscribeStore(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Test-only: wipe every app's store (module state leaks across test cases). */
export function __resetStores(): void {
  for (const k of Object.keys(stores)) delete stores[k];
}
